import apiClient from '../api/apiClient';

interface SystemInfo {
  server: {
    hostname: string;
    uptime: number;
    loadAverage: number[];
    platform: string;
    arch: string;
  };
  memory: {
    total: number;
    used: number;
    free: number;
    percentage: number;
  };
  disk: {
    total: number;
    used: number;
    free: number;
    percentage: number;
  };
  services: {
    name: string;
    status: 'online' | 'stopped' | 'errored';
    cpu: number;
    memory: number;
    restarts: number;
    uptime: string;
  }[];
  database: {
    size: number;
    tables: number;
    records: {
      users: number;
      stocks: number;
      companies: number;
    };
  };
  deployment: {
    currentCommit: string;
    currentBranch: string;
    lastDeployment: string;
    githubCommit: string;
    isUpToDate: boolean;
    behind: number;
  };
}

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
  children?: FileNode[];
}

interface DeploymentResult {
  success: boolean;
  error?: string;
  output?: string;
}

export interface AdminNotificationCounts {
  feedback: number;
  users: number;
  revocations: number;
}

class SystemService {
  async getSystemInfo(timeRange: '1h' | '24h' | '7d' | '30d' | 'all' = '24h'): Promise<SystemInfo> {
    try {
      const response = await apiClient.get('/admin/system/info', {
        params: { timeRange }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async listDirectory(path: string): Promise<FileNode[]> {
    try {
      const response = await apiClient.get('/admin/system/files', {
        params: { path }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to list directory:', error);
      throw error;
    }
  }

  async readFile(path: string): Promise<{ content: string; isImage?: boolean; mimeType?: string }> {
    try {
      const response = await apiClient.get('/admin/system/file', {
        params: { path }
      });
      return {
        content: response.data.content,
        isImage: response.data.isImage,
        mimeType: response.data.mimeType
      };
    } catch (error) {
      console.error('Failed to read file:', error);
      throw error;
    }
  }

  async getLogs(type: 'backend' | 'frontend' | 'nginx' | 'combined', lines: number = 50): Promise<string[]> {
    try {
      const response = await apiClient.get('/admin/system/logs', {
        params: { type, lines }
      });
      return response.data.logs;
    } catch (error) {
      console.error('Failed to get logs:', error);
      throw error;
    }
  }

  async deployLatest(): Promise<DeploymentResult> {
    try {
      const response = await apiClient.post('/admin/system/deploy');
      return response.data;
    } catch (error) {
      console.error('Deployment failed:', error);
      throw error;
    }
  }

  async getServiceDetails(serviceName: string): Promise<any> {
    try {
      const response = await apiClient.get(`/admin/system/service/${serviceName}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get service details:', error);
      throw error;
    }
  }

  async restartService(serviceName: string): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post('/admin/system/restart', {
        service: serviceName
      });
      return response.data;
    } catch (error) {
      console.error('Failed to restart service:', error);
      throw error;
    }
  }

  async runCommand(command: string): Promise<{ output: string; error?: string }> {
    try {
      const response = await apiClient.post('/admin/system/command', {
        command
      });
      return response.data;
    } catch (error) {
      console.error('Failed to run command:', error);
      throw error;
    }
  }

  async saveFile(filePath: string, content: string): Promise<any> {
    try {
      const response = await apiClient.post('/admin/system/file', {
        path: filePath,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save file:', error);
      throw error;
    }
  }

  async deleteFile(filePath: string): Promise<any> {
    try {
      const response = await apiClient.delete(`/admin/system/file?path=${encodeURIComponent(filePath)}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  async getDatabaseInfo(): Promise<any> {
    try {
      const response = await apiClient.get('/admin/system/database-info');
      return response.data;
    } catch (error) {
      console.error('Failed to get database info:', error);
      throw error;
    }
  }

  async getTableData(
    tableName: string, 
    page: number = 1, 
    limit: number = 100,
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
    search?: string
  ): Promise<any> {
    try {
      const params: any = { page, limit };
      if (sortColumn) params.sortColumn = sortColumn;
      if (sortDirection) params.sortDirection = sortDirection;
      if (search) params.search = search;
      
      const response = await apiClient.get(`/admin/system/table/${tableName}`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to get table data:', error);
      throw error;
    }
  }

  async updateTableRow(tableName: string, rowData: any): Promise<any> {
    try {
      const response = await apiClient.put(`/admin/system/table/${tableName}`, rowData);
      return response.data;
    } catch (error) {
      console.error('Failed to update table row:', error);
      throw error;
    }
  }

  async insertTableRow(tableName: string, rowData: any): Promise<any> {
    try {
      const response = await apiClient.post(`/admin/system/table/${tableName}`, rowData);
      return response.data;
    } catch (error) {
      console.error('Failed to insert table row:', error);
      throw error;
    }
  }

  async deleteTableRow(tableName: string, rowData: any): Promise<any> {
    try {
      const response = await apiClient.delete(`/admin/system/table/${tableName}`, { data: rowData });
      return response.data;
    } catch (error) {
      console.error('Failed to delete table row:', error);
      throw error;
    }
  }

  async deleteAllStockPricesBySymbol(symbol: string): Promise<any> {
    try {
      const response = await apiClient.delete(`/admin/system/stock-prices/symbol/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete stock prices by symbol:', error);
      throw error;
    }
  }

  async getBannedIPs(): Promise<any> {
    try {
      const response = await apiClient.get('/admin/system/security/banned-ips');
      return response.data;
    } catch (error) {
      console.error('Failed to get banned IPs:', error);
      throw error;
    }
  }

  async unbanIP(ip: string, jail: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post('/admin/system/security/unban', { ip, jail });
      return response.data;
    } catch (error) {
      console.error('Failed to unban IP:', error);
      throw error;
    }
  }

  async getSecurityStats(): Promise<any> {
    try {
      const response = await apiClient.get('/admin/system/security/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to get security stats:', error);
      throw error;
    }
  }

  async shutdownPi(): Promise<{ success: boolean; message: string; hostname?: string; error?: string }> {
    try {
      const response = await apiClient.post('/admin/system/shutdown', {
        confirm: 'SHUTDOWN_PI'
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to shutdown Pi:', error);
      // Return the error message from the server
      if (error.response?.data) {
        return error.response.data;
      }
      throw error;
    }
  }
  async getAdminNotificationCounts(): Promise<AdminNotificationCounts> {
    const response = await apiClient.get('/admin/notification-counts');
    return response.data.data;
  }

  async markSectionSeen(section: string): Promise<void> {
    await apiClient.post(`/admin/mark-seen/${section}`);
  }
}

export const systemService = new SystemService();